import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Send, Loader2, Trash2, Lightbulb, ChefHat, Sparkles, MessageCircle, X, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function ChefHediPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showAbout, setShowAbout] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const savedSession = localStorage.getItem('chef_mehdi_session');
    if (savedSession) {
      setSessionId(savedSession);
      loadHistory(savedSession);
    }
    loadSuggestions();
  }, []);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const loadHistory = async (sid) => {
    try {
      const response = await api.get(`/chef-mehdi/history/${sid}`);
      if (response.data.messages?.length) {
        setMessages(response.data.messages.map(m => ({
          role: m.role,
          content: m.content
        })));
      }
    } catch (error) {
      console.log('No history found');
    }
  };

  const loadSuggestions = async () => {
    try {
      const response = await api.get('/chef-mehdi/suggestions');
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      setSuggestions([
        "Comment faire un bon couscous tunisien?",
        "Quelle est la recette du brik à l'oeuf?",
        "Comment préparer la harissa maison?"
      ]);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/chef-mehdi/chat', {
        message: text,
        session_id: sessionId
      });

      const newSessionId = response.data.session_id;
      if (!sessionId && newSessionId) {
        setSessionId(newSessionId);
        localStorage.setItem('chef_mehdi_session', newSessionId);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      toast.error(t('chefMehdi.errorMessage'));
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await api.delete(`/chef-mehdi/history/${sessionId}`);
      } catch (error) {}
    }
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('chef_mehdi_session');
    toast.success(t('chefMehdi.chatCleared'));
  };

  return (
    <div
      className="chat-page-height flex flex-col overflow-hidden"
      data-testid="chef-mehdi-page"
    >
      {/* Chat container */}
      <div className="flex flex-col flex-1 bg-white overflow-hidden min-h-0">

        {/* Compact chat header */}
        <div className="bg-gradient-to-r from-[#1E4ED8] to-[#0D3A9E] px-4 py-3 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-xl shadow-md shrink-0">
              👨‍🍳
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-fraunces font-bold text-base leading-tight">Chef Mehdi</h1>
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI
                </span>
              </div>
              <p className="text-white/70 text-xs leading-tight mt-0.5">
                {t('chefMehdi.subtitle')}
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowAbout(true)}
            className="px-3 py-1.5 bg-white/20 hover:bg-white/30 active:bg-white/40 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 shrink-0"
          >
            <Info className="w-3.5 h-3.5" />
            {t('chefMehdi.aboutButton')}
          </button>
        </div>

        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-16 h-16 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center mb-4">
                <MessageCircle className="w-8 h-8 text-[#1E4ED8]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {t('chefMehdi.greeting')}
              </h3>
              <p className="text-gray-500 text-sm mb-6 max-w-sm">
                {t('chefMehdi.greetingDesc')}
              </p>

              {/* Suggestions */}
              <div className="space-y-2 w-full max-w-md">
                <p className="text-xs text-gray-400 flex items-center gap-1 justify-center">
                  <Lightbulb className="w-3 h-3" />
                  {t('chefMehdi.suggestions')}
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestions.slice(0, 4).map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(suggestion)}
                      className="px-3 py-2 bg-gray-50 hover:bg-[#1E4ED8]/5 border border-gray-200 hover:border-[#1E4ED8]/30 rounded-xl text-sm text-gray-700 transition-colors text-left"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#1E4ED8] text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-900 rounded-bl-md'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">👨‍🍳</span>
                        <span className="text-xs font-medium text-[#1E4ED8]">{t('chefMehdi.senderLabel')}</span>
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">👨‍🍳</span>
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-[#1E4ED8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-[#1E4ED8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-[#1E4ED8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Chat footer */}
        <div className="border-t border-gray-100 p-4 shrink-0 bg-white">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chefMehdi.inputPlaceholder')}
              className="flex-1 h-12 rounded-xl"
              disabled={loading}
              data-testid="chef-mehdi-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-12 px-6 bg-[#1E4ED8] hover:bg-[#1842B8] rounded-xl shrink-0"
              data-testid="chef-mehdi-send"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </form>

          {messages.length > 0 && (
            <div className="flex justify-center mt-3">
              <button
                onClick={clearChat}
                className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
                {t('chefMehdi.clearChat')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* About modal */}
      {showAbout && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
          onClick={() => setShowAbout(false)}
        >
          <div
            className="bg-white rounded-t-2xl p-6 w-full max-w-lg shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1E4ED8]/10 rounded-lg">
                  <ChefHat className="w-5 h-5 text-[#1E4ED8]" />
                </div>
                <h4 className="font-semibold text-gray-900">{t('chefMehdi.aboutTitle')}</h4>
              </div>
              <button
                onClick={() => setShowAbout(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1E4ED8] to-[#0D3A9E] flex items-center justify-center text-2xl shrink-0 shadow-md">
                👨‍🍳
              </div>
              <div>
                <p className="font-medium text-gray-900">Chef Mehdi</p>
                <p className="text-sm text-gray-500">{t('chefMehdi.subtitle')}</p>
                <p className="text-sm text-[#1E4ED8] mt-1 italic">
                  "{t('chefMehdi.quote')}"
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              {t('chefMehdi.aboutDesc')}
            </p>

            <button
              onClick={() => setShowAbout(false)}
              className="mt-5 w-full py-3 bg-[#1E4ED8] hover:bg-[#1842B8] text-white rounded-xl font-medium transition-colors text-sm"
            >
              {t('chefMehdi.startCooking')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
